import React, { useEffect, useState, useCallback } from 'react';
import {
  BookOpen,
  CalendarClock,
  CheckCircle2,
  FileQuestion,
  Megaphone,
  Phone,
  PhoneCall,
  RefreshCw,
  Tag,
  Users2,
} from 'lucide-react';
import { campaignsService, type Campaign } from '@/services/api/campaigns';
import { useToast } from '@/components/ui';
import TriggerAiCallModal from './modals/TriggerAiCallModal';

// ── status config ─────────────────────────────────────────────────────────────
const STATUS_CFG: Record<
  Campaign['status'],
  { label: string; dot: string; badge: string }
> = {
  active: {
    label: 'Active',
    dot: 'bg-green-500',
    badge: 'bg-green-100 text-green-700 border-green-200',
  },
  inactive: {
    label: 'Inactive',
    dot: 'bg-gray-400',
    badge: 'bg-gray-100 text-gray-500 border-gray-200',
  },
  draft: {
    label: 'Draft',
    dot: 'bg-yellow-400',
    badge: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  },
  completed: {
    label: 'Completed',
    dot: 'bg-blue-400',
    badge: 'bg-blue-50 text-blue-700 border-blue-200',
  },
};

// ── Tiny skeleton card ────────────────────────────────────────────────────────
const SkeletonCard: React.FC = () => (
  <div className="bg-white border border-gray-200 rounded-2xl p-5 animate-pulse space-y-4">
    <div className="flex items-start justify-between">
      <div className="space-y-2 flex-1">
        <div className="h-4 bg-gray-200 rounded w-3/4" />
        <div className="h-3 bg-gray-100 rounded w-1/2" />
      </div>
      <div className="w-16 h-6 bg-gray-100 rounded-full" />
    </div>
    <div className="h-3 bg-gray-100 rounded w-full" />
    <div className="h-3 bg-gray-100 rounded w-5/6" />
    <div className="flex gap-2 mt-2">
      <div className="h-5 w-16 bg-indigo-50 rounded-full" />
      <div className="h-5 w-16 bg-indigo-50 rounded-full" />
    </div>
    <div className="h-9 bg-gray-100 rounded-xl w-full mt-2" />
  </div>
);

// ── Campaign Card ─────────────────────────────────────────────────────────────
interface CampaignCardProps {
  campaign: Campaign;
  onTriggerCall: (c: Campaign) => void;
}

const CampaignCard: React.FC<CampaignCardProps> = ({ campaign, onTriggerCall }) => {
  const cfg = STATUS_CFG[campaign.status];

  return (
    <div className="group relative bg-white border border-gray-200 hover:border-indigo-300 rounded-2xl p-5 transition-all duration-200 hover:shadow-md flex flex-col gap-4">
      {/* Top row: name + status */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2.5 flex-1 min-w-0">
          <div className="mt-0.5 w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0">
            <Megaphone size={15} className="text-indigo-600" />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900 text-sm leading-snug truncate">
              {campaign.name}
            </h3>
            {campaign.description && (
              <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">
                {campaign.description}
              </p>
            )}
          </div>
        </div>

        {/* Status badge */}
        <span
          className={`shrink-0 flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${cfg.badge}`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
          {cfg.label}
        </span>
      </div>

      {/* Meta row */}
      <div className="grid grid-cols-2 gap-2">
        {/* Lead categories */}
        <div className="flex items-start gap-1.5 col-span-2">
          <Tag size={12} className="text-indigo-400 shrink-0 mt-0.5" />
          <div className="flex flex-wrap gap-1">
            {campaign.lead_categories.length > 0 ? (
              campaign.lead_categories.map((cat) => (
                <span
                  key={cat}
                  className="text-[11px] font-medium px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 capitalize"
                >
                  {cat.replace('_', ' ')}
                </span>
              ))
            ) : (
              <span className="text-xs text-gray-400">No categories</span>
            )}
          </div>
        </div>

        {/* Script questions count */}
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <FileQuestion size={12} className="text-gray-400" />
          <span>
            <strong className="text-gray-700">{campaign.script.length}</strong>{' '}
            question{campaign.script.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Agents */}
        <div className="flex items-center gap-1.5 text-xs text-gray-500">
          <Users2 size={12} className="text-gray-400" />
          <span>
            <strong className="text-gray-700">{campaign.assigned_agent_ids.length}</strong>{' '}
            agent{campaign.assigned_agent_ids.length !== 1 ? 's' : ''}
          </span>
        </div>

        {/* Scheduled at */}
        {campaign.scheduled_at && (
          <div className="col-span-2 flex items-center gap-1.5 text-xs text-gray-500">
            <CalendarClock size={12} className="text-gray-400" />
            <span>
              Scheduled:{' '}
              <strong className="text-gray-700">
                {new Date(campaign.scheduled_at).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </strong>
            </span>
          </div>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-gray-100" />

      {/* Script questions preview */}
      {campaign.script.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5 flex items-center gap-1">
            <BookOpen size={11} />
            Script
          </p>
          <div className="space-y-1">
            {campaign.script.slice(0, 2).map((q, idx) => (
              <div
                key={q.id}
                className="flex items-start gap-2 text-xs text-gray-600"
              >
                <span className="font-bold text-indigo-400 shrink-0">Q{idx + 1}.</span>
                <span className="line-clamp-1">{q.question}</span>
              </div>
            ))}
            {campaign.script.length > 2 && (
              <p className="text-[11px] text-gray-400 pl-5">
                +{campaign.script.length - 2} more question{campaign.script.length - 2 !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>
      )}

      {/* CTA — Trigger AI Call */}
      <button
        onClick={() => onTriggerCall(campaign)}
        disabled={campaign.status === 'completed' || campaign.status === 'inactive'}
        className="mt-auto w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-xl py-2.5 text-sm transition-all duration-150 active:scale-95 shadow-sm hover:shadow-md"
      >
        <PhoneCall size={15} />
        {campaign.status === 'completed'
          ? 'Campaign Completed'
          : campaign.status === 'inactive'
          ? 'Campaign Inactive'
          : 'Start AI Call'}
      </button>
    </div>
  );
};

// ── Empty state ───────────────────────────────────────────────────────────────
const EmptyState: React.FC = () => (
  <div className="flex flex-col items-center justify-center text-center py-20 gap-4">
    <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center">
      <Megaphone size={28} className="text-indigo-300" />
    </div>
    <div>
      <h3 className="font-bold text-gray-700 text-base">No Campaigns Assigned</h3>
      <p className="text-gray-400 text-sm mt-1 max-w-xs">
        You haven't been assigned to any campaigns yet. Contact your admin to get started.
      </p>
    </div>
  </div>
);

// ── Summary stats ─────────────────────────────────────────────────────────────
const StatPill: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: number;
  color: string;
}> = ({ icon, label, value, color }) => (
  <div className={`flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-medium ${color}`}>
    {icon}
    <span className="font-bold text-base">{value}</span>
    <span>{label}</span>
  </div>
);

// ── Main Page ─────────────────────────────────────────────────────────────────
const MyCampaignsPage: React.FC = () => {
  const { error: toastError } = useToast();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [showCallModal, setShowCallModal] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await campaignsService.getMyAssignedCampaigns();
      const list: Campaign[] =
        res?.attributes?.campaigns ?? res?.data ?? [];
      setCampaigns(list);
    } catch (err: any) {
      toastError(err?.message || 'Failed to load your campaigns');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const handleTriggerCall = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setShowCallModal(true);
  };

  // Summary counts
  const activeCnt = campaigns.filter((c) => c.status === 'active').length;
  const completedCnt = campaigns.filter((c) => c.status === 'completed').length;
  const totalQuestions = campaigns.reduce((sum, c) => sum + c.script.length, 0);

  return (
    <div className="w-full">
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone size={22} className="text-indigo-600" />
            My Campaigns
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-0.5">
            Campaigns assigned to you — click <strong>"Start AI Call"</strong> to begin outreach.
          </p>
        </div>

        {/* Refresh */}
        <button
          onClick={fetchCampaigns}
          disabled={isLoading}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-indigo-600 border border-gray-200 hover:border-indigo-300 px-3 py-2 rounded-lg transition-colors disabled:opacity-50"
        >
          <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* ── Summary pills ──────────────────────────────────────────────────── */}
      {!isLoading && campaigns.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          <StatPill
            icon={<Megaphone size={13} />}
            label="Total"
            value={campaigns.length}
            color="bg-white border-gray-200 text-gray-600"
          />
          <StatPill
            icon={<Phone size={13} />}
            label="Active"
            value={activeCnt}
            color="bg-green-50 border-green-200 text-green-700"
          />
          <StatPill
            icon={<CheckCircle2 size={13} />}
            label="Completed"
            value={completedCnt}
            color="bg-blue-50 border-blue-200 text-blue-700"
          />
          <StatPill
            icon={<FileQuestion size={13} />}
            label="Total Questions"
            value={totalQuestions}
            color="bg-indigo-50 border-indigo-200 text-indigo-700"
          />
        </div>
      )}

      {/* ── Content ───────────────────────────────────────────────────────── */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 3 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : campaigns.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {campaigns.map((c) => (
            <CampaignCard
              key={c.uuid}
              campaign={c}
              onTriggerCall={handleTriggerCall}
            />
          ))}
        </div>
      )}

      {/* ── Trigger AI Call Modal ─────────────────────────────────────────── */}
      <TriggerAiCallModal
        isOpen={showCallModal}
        onClose={() => {
          setShowCallModal(false);
          setSelectedCampaign(null);
        }}
        campaign={selectedCampaign}
      />
    </div>
  );
};

export default MyCampaignsPage;
